import { useMemo, useState } from 'react';
import { LayoutChangeEvent, Pressable, Text, View, useWindowDimensions } from 'react-native';
import type { RoomLayoutSpot } from './RoomLayoutMap.types';
import { SpotStatus } from './RoomLayoutMap.types';
import {
  hexWithAlpha,
  isValidHexColor,
  normalizeHex,
  SELECTED_SPOT_FILL_ALPHA_HEX
} from '../../colors/hexColor';
import {
  getSpotCellEmptyStyle,
  getSpotCellStyle,
  getSpotHighlightKind,
  getSpotLabelStyle,
  getSpotSizeForWidth,
  GRID_GAP,
  GRID_H_PADDING,
  GRID_PADDING_TOP,
  SPOT_BASE_SIZE,
  SpotHighlightKind,
  styles
} from './RoomLayoutGrid.styles';
import {
  getNonBookableSpotIcon,
  getNonBookableSpotIconAspectRatio,
  trimSpotTypePrefix,
  shouldRenderNonBookableSpotTypeIcon
} from './nonBookableSpotTypeIcons';
import { inferNotBookablePrefixFromDisplayName } from './spotTypeEnrichment';

const BOOKABLE_LABEL_MAX = 4;
const NOT_BOOKABLE_LABEL_MAX = 9;

export type RoomLayoutGridProps = {
  columns: number;
  rows: number;
  spots: RoomLayoutSpot[];
  selectedSpotKey?: string | null;
  /** @deprecated Use `selectedSpotKey`. Kept until RoomLayoutMap passes the renamed prop. */
  selectedSpotName?: string | null;
  assignedSpotKey?: string | null;
  /** Optional: hex color for the selected spot (outline + transparent fill). When set, overrides default. */
  selectedSpotColor?: string;
  /** When false, custom non-bookable types use the legacy trainer icon instead of prefix text. */
  useCustomNonBookablePrefixes?: boolean;
  /** Called when the user taps an available (BOOKABLE) spot. Not called for BLOCKED or NOT_BOOKABLE. */
  onSpotPress?: (spot: RoomLayoutSpot) => void;
  /**
   * When true, use fixed spot size (SPOT_BASE_SIZE) and explicit wrapper dimensions so the grid
   * can be placed inside a horizontal ScrollView and scroll without resizing spots.
   */
  scrollable?: boolean;
  /** When set, overrides spot size in scrollable mode. Used with scrollable or on its own. */
  fixedSpotSize?: number;
};

/**
 * API convention (1-based, matches MongoDB room_layouts):
 * - layout.rows = number of display rows (vertical lines)
 * - layout.columns = number of display columns (horizontal positions per row)
 * - spot.row = which display row (1..rows)
 * - spot.column = which display column (1..columns)
 *
 * grid[displayRow][displayCol] with displayRow in [0..rows-1], displayCol in [0..columns-1].
 * Mapping: displayRow = spot.row - 1, displayCol = spot.column - 1.
 *
 * Example: layout rows=2, columns=20 → 2 rows × 20 columns. Spot (column: 5, row: 1) → grid[0][4].
 */
function buildGrid(
  columns: number,
  rows: number,
  spots: RoomLayoutSpot[]
): (RoomLayoutSpot | null)[][] {
  const grid: (RoomLayoutSpot | null)[][] = Array.from({ length: rows }, () =>
    Array.from({ length: columns }, () => null)
  );
  for (const spot of spots) {
    const displayRow = spot.row - 1;
    const displayCol = spot.column - 1;
    const row = grid[displayRow];
    if (row && displayCol >= 0 && displayCol < columns && displayRow >= 0 && displayRow < rows) {
      row[displayCol] = spot;
    }
  }
  return grid;
}

export default function RoomLayoutGrid({
  columns,
  rows,
  spots,
  selectedSpotKey: selectedSpotKeyProp = null,
  selectedSpotName = null,
  assignedSpotKey = null,
  selectedSpotColor,
  onSpotPress,
  scrollable = false,
  fixedSpotSize,
  useCustomNonBookablePrefixes = false
}: RoomLayoutGridProps) {
  const selectedSpotKey = selectedSpotKeyProp ?? selectedSpotName;
  const { width: windowWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState<number>(windowWidth);

  const onLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  /** Full operator grid: empty coordinates stay as invisible spacers (gaps), never trimmed. */
  const grid = useMemo(() => buildGrid(columns, rows, spots), [columns, rows, spots]);
  const displayColumns = grid[0]?.length ?? columns;
  const displayRows = grid.length;

  const useFixedSize = scrollable || typeof fixedSpotSize === 'number';
  // `selectedSpotColor` is constant for the whole grid render; compute this once.
  const resolvedSpotColor = useMemo(
    () => (isValidHexColor(selectedSpotColor) ? normalizeHex(selectedSpotColor) : undefined),
    [selectedSpotColor]
  );
  const spotSize = useMemo(() => {
    if (!useFixedSize) {
      return getSpotSizeForWidth(containerWidth, displayColumns);
    }
    if (typeof fixedSpotSize === 'number') {
      return fixedSpotSize;
    }
    return SPOT_BASE_SIZE;
  }, [useFixedSize, fixedSpotSize, containerWidth, displayColumns]);

  const wrapperStyle = useMemo(() => {
    if (!useFixedSize) return styles.gridWrapper;
    const contentWidth =
      displayColumns * spotSize + (displayColumns - 1) * GRID_GAP + 2 * GRID_H_PADDING;
    const contentHeight = displayRows * spotSize + (displayRows - 1) * GRID_GAP + GRID_PADDING_TOP;
    return [
      styles.gridWrapper,
      styles.gridWrapperScrollable,
      { width: contentWidth, minHeight: contentHeight }
    ];
  }, [useFixedSize, displayColumns, displayRows, spotSize]);

  return (
    <View
      onLayout={useFixedSize ? undefined : onLayout}
      style={wrapperStyle}
      testID="room-layout-grid-wrapper"
    >
      <View style={styles.gridContent} testID="room-layout-grid">
        {grid.map((rowSpots, rowIndex) => (
          <View
            key={`row-${String(rowIndex)}`}
            style={styles.row}
            testID={`room-layout-grid-row-${String(rowIndex)}`}
          >
            {rowSpots.map((spot, colIndex) => {
              if (!spot || spot.status === SpotStatus.EMPTY) {
                return (
                  <View
                    key={`empty-${String(rowIndex)}-${String(colIndex)}`}
                    style={getSpotCellEmptyStyle(spotSize)}
                    accessible={false}
                    accessibilityElementsHidden
                    importantForAccessibility="no"
                    testID={`room-layout-spot-empty-${String(rowIndex)}-${String(colIndex)}`}
                  />
                );
              }
              let highlight: SpotHighlightKind = getSpotHighlightKind(
                spot.spotName,
                selectedSpotKey,
                assignedSpotKey
              );
              const shouldKeepHighlight =
                highlight === SpotHighlightKind.None ||
                highlight === SpotHighlightKind.Assigned ||
                (spot.status !== SpotStatus.BLOCKED && spot.status !== SpotStatus.NOT_BOOKABLE);
              if (!shouldKeepHighlight) {
                highlight = SpotHighlightKind.None;
              }
              const selectedForStyling = highlight !== SpotHighlightKind.None;
              const useStudioColor =
                assignedSpotKey === null &&
                highlight === SpotHighlightKind.LegacySelected &&
                resolvedSpotColor !== undefined;
              const isBookable = spot.status === SpotStatus.BOOKABLE;
              const handlePress = () => {
                if (onSpotPress) onSpotPress(spot);
              };
              const displayLabel =
                spot.status === SpotStatus.NOT_BOOKABLE
                  ? spot.spot_name.slice(0, NOT_BOOKABLE_LABEL_MAX)
                  : spot.spot_name.slice(0, BOOKABLE_LABEL_MAX);
              const isNotBookable = spot.status === SpotStatus.NOT_BOOKABLE;
              const useNonBookableIcon =
                isNotBookable &&
                shouldRenderNonBookableSpotTypeIcon(
                  spot.spot_type_id,
                  spot.spot_type_prefix,
                  spot.spot_type_name,
                  useCustomNonBookablePrefixes
                );
              const nonBookablePrefixLabel =
                isNotBookable && !useNonBookableIcon
                  ? (trimSpotTypePrefix(spot.spot_type_prefix) ??
                    trimSpotTypePrefix(
                      inferNotBookablePrefixFromDisplayName(spot.spot_type_name, spot.spot_name) ??
                        undefined
                    ))
                  : null;
              const iconWidth = Math.min(17, Math.max(10, Math.floor(spotSize * 0.28)));
              const iconAspectRatio = getNonBookableSpotIconAspectRatio(
                spot.spot_type_id,
                spot.spot_type_name,
                spot.spot_type_prefix
              );
              const iconHeight = Math.round(iconWidth * iconAspectRatio);
              const nonBookableIcon = useNonBookableIcon
                ? getNonBookableSpotIcon(
                    spot.spot_type_id,
                    {
                      width: iconWidth,
                      height: iconHeight
                    },
                    spot.spot_type_name,
                    spot.spot_type_prefix
                  )
                : null;
              const cellStyle = useStudioColor
                ? [
                    ...getSpotCellStyle(spot.status, highlight, spotSize),
                    {
                      borderColor: resolvedSpotColor,
                      backgroundColor: hexWithAlpha(resolvedSpotColor, SELECTED_SPOT_FILL_ALPHA_HEX)
                    }
                  ]
                : getSpotCellStyle(spot.status, highlight, spotSize);
              const labelStyle = useStudioColor
                ? [
                    ...getSpotLabelStyle(spot.status, highlight, spotSize),
                    { color: resolvedSpotColor }
                  ]
                : getSpotLabelStyle(spot.status, highlight, spotSize);
              const cell = (
                <View
                  style={cellStyle}
                  testID={`room-layout-spot-${spot.spotName}`}
                  accessibilityState={{ selected: selectedForStyling }}
                >
                  {highlight === SpotHighlightKind.Assigned && (
                    <View
                      testID={`room-layout-spot-${spot.spotName}-assigned`}
                      collapsable={false}
                    />
                  )}
                  {(highlight === SpotHighlightKind.Active ||
                    highlight === SpotHighlightKind.LegacySelected) && (
                    <View
                      testID={`room-layout-spot-${spot.spotName}-selected`}
                      collapsable={false}
                    />
                  )}
                  {nonBookableIcon !== null || nonBookablePrefixLabel !== null ? (
                    <View style={styles.spotCellNotBookableWithIcon}>
                      {nonBookableIcon !== null ? (
                        <View
                          testID={`room-layout-non-bookable-icon-${spot.spotName}`}
                          collapsable={false}
                        >
                          {nonBookableIcon}
                        </View>
                      ) : (
                        <Text
                          style={styles.spotTypePrefixLabel}
                          numberOfLines={1}
                          testID={`room-layout-non-bookable-prefix-${spot.spotName}`}
                        >
                          {nonBookablePrefixLabel}
                        </Text>
                      )}
                      <Text style={labelStyle} numberOfLines={1}>
                        {displayLabel}
                      </Text>
                    </View>
                  ) : (
                    <Text style={labelStyle} numberOfLines={1}>
                      {displayLabel}
                    </Text>
                  )}
                </View>
              );
              return (
                <Pressable
                  key={`${spot.spotName}-${String(rowIndex)}-${String(colIndex)}`}
                  onPress={handlePress}
                  disabled={!isBookable}
                  testID={`room-layout-spot-pressable-${spot.spotName}`}
                >
                  {cell}
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}
